import { useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import { useSystemStore } from '../store/useSystemStore';
import { LogIn, UserPlus, Mail, Lock, User, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDiagnosticStore } from '../store/useDiagnosticStore';
import VerboLogo from '../components/VerboLogo';

export default function Auth() {
  const { 
    user, 
    loginWithGoogle, 
    loginWithEmail, 
    registerWithEmail, 
    resetPassword, 
    loading, 
    loginLoading,
    isGoogleSigningIn,
    googleAuthMessage,
    clearGoogleAuthMessage
  } = useAuthStore();
  const diag = useDiagnosticStore();
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset'>('login');
  
  console.log('[Auth] Render:', { user: !!user, loading, loginLoading, authMode });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400 font-mono text-xs uppercase tracking-widest">Cargando...</div>;
  
  if (user) {
    console.log('[Auth] User detected, redirecting to /');
    return <Navigate to="/" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (authMode === 'reset') {
      if (!email.trim()) {
        toast.error('Por favor, introduce tu email');
        return;
      }
      setIsSubmitting(true);
      try {
        await resetPassword(email.trim());
        toast.success('Si existe una cuenta con ese email, te enviaremos un enlace para crear una nueva contraseña.');
        setAuthMode('login');
      } catch (error: any) {
        console.error("Reset error:", error);
        toast.error('Error al enviar el correo de recuperación');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!email || !password || (authMode === 'register' && !nickname)) {
      toast.error('Por favor, rellena todos los campos');
      return;
    }

    setIsSubmitting(true);
    try {
      if (authMode === 'login') {
        clearGoogleAuthMessage();
        await loginWithEmail(email, password);
        toast.success('¡Bienvenido de nuevo!');
      } else {
        await registerWithEmail(email, password, nickname);
        toast.success('¡Cuenta creada con éxito!');
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      let message = 'Ocurrió un error inesperado';
      if (error.code === 'auth/network-request-failed') {
        message = 'Error de red: La conexión fue bloqueada. Prueba a desactivar bloqueadores de anuncios o abre la app en una pestaña nueva.';
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'Email o contraseña incorrectos';
      } else if (error.code === 'auth/email-already-in-use') {
        message = 'Este email ya está registrado';
      } else if (error.code === 'auth/weak-password') {
        message = 'La contraseña es demasiado débil';
      } else if (error.code === 'auth/invalid-email') {
        message = 'El formato del email no es válido';
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-app-bg flex flex-col items-center justify-center p-4 sm:p-6 font-sans overflow-hidden">
      <div className="w-full max-w-md space-y-4 sm:space-y-6">
        {/* Logo Section - More Compact */}
        <div className="text-center space-y-1">
          <div className="flex flex-col items-center scale-90 sm:scale-100">
            <VerboLogo size={56} />
          </div>
          <p className="text-muted font-black uppercase tracking-[0.25em] text-[8px] sm:text-[9px]">El juego de palabras definitivo</p>
        </div>
 
        {/* Auth Card - Compacted */}
        <div className="bg-card-bg border border-grid-line rounded-[2rem] p-5 sm:p-7 shadow-card space-y-4 sm:space-y-5">
          {authMode !== 'reset' && (
            <>
              {/* Primary Action: Google */}
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  diag.incrementGoogleClicks();
                  diag.addLog('REAL_GOOGLE_HANDLER_ENTER');
                  if (loginLoading || isGoogleSigningIn || isSubmitting) return;
                  try {
                    clearGoogleAuthMessage();
                    await loginWithGoogle();
                  } catch (error: any) {}
                }}
                disabled={isSubmitting || loginLoading || isGoogleSigningIn}
                className="w-full bg-primary-accent text-white font-black py-4 rounded-xl flex items-center justify-center gap-3 transition-all text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-primary-accent/20 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting || loginLoading || isGoogleSigningIn ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.27.81-.57z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continuar con Google
                  </>
                )}
              </button>
 
              {googleAuthMessage && (
                <div className="rounded-2xl border border-mult-tp/20 bg-mult-tp/10 p-3 space-y-3">
                  <p className="text-[11px] font-bold leading-relaxed text-text-primary">{googleAuthMessage}</p>
                  <button
                    type="button"
                    onClick={() => {
                      clearGoogleAuthMessage();
                      setAuthMode('login');
                    }}
                    className="w-full rounded-xl bg-card-bg border border-grid-line py-2.5 text-[9px] font-black uppercase tracking-widest text-secondary"
                  >
                    Usar email y contraseña
                  </button>
                </div>
              )}

              {/* Separator */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-grid-line/50"></div>
                </div>
                <div className="relative flex justify-center text-[8px] sm:text-[9px] uppercase tracking-[0.2em] font-black">
                  <span className="bg-card-bg px-3 text-muted">o usa tu email</span>
                </div>
              </div>
            </>
          )}
 
          {authMode === 'reset' && (
            <div className="text-center space-y-1 py-1">
              <h2 className="text-base font-black text-primary tracking-tight">Recuperar Contraseña</h2>
              <p className="text-[10px] text-muted font-medium">Si existe una cuenta con ese email, te enviaremos un enlace para crear una nueva contraseña.</p>
            </div>
          )}
 
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {authMode === 'register' && (
              <div className="space-y-1.5">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                  <input 
                    type="text" 
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Nickname"
                    className="w-full bg-board-bg border border-grid-line rounded-xl pl-10 pr-4 py-3 text-xs text-primary focus:outline-none focus:border-primary-accent transition-all"
                    maxLength={25}
                  />
                </div>
              </div>
            )}
 
            <div className="space-y-1.5">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full bg-board-bg border border-grid-line rounded-xl pl-10 pr-4 py-3 text-xs text-primary focus:outline-none focus:border-primary-accent transition-all"
                />
              </div>
            </div>
 
            {authMode !== 'reset' && (
              <div className="space-y-1.5">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Contraseña"
                    className="w-full bg-board-bg border border-grid-line rounded-xl pl-10 pr-10 py-3 text-xs text-primary focus:outline-none focus:border-primary-accent transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-primary-accent transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {authMode === 'login' && (
                  <div className="flex justify-end">
                    <button 
                      type="button"
                      onClick={() => setAuthMode('reset')}
                      className="text-[9px] font-bold text-primary-accent uppercase tracking-widest hover:opacity-80 transition-colors"
                    >
                      ¿Olvidaste tu clave?
                    </button>
                  </div>
                )}
              </div>
            )}
 
            <button
              type="submit"
              disabled={isSubmitting || loginLoading}
              className="w-full bg-board-bg hover:bg-grid-line/20 text-secondary border border-grid-line font-black py-3.5 rounded-xl flex items-center justify-center gap-3 transition-all text-[10px] uppercase tracking-[0.2em] shadow-sm disabled:opacity-50"
            >
              {isSubmitting || loginLoading ? (
                <div className="w-4 h-4 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {authMode === 'login' ? <LogIn className="w-3.5 h-3.5" /> : authMode === 'register' ? <UserPlus className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
                  {authMode === 'login' ? 'Entrar con Email' : authMode === 'register' ? 'Registrarse' : 'Enviar Enlace'}
                </>
              )}
            </button>
 
            {authMode === 'reset' && (
              <button 
                type="button"
                onClick={() => setAuthMode('login')}
                className="w-full text-[9px] font-bold text-muted uppercase tracking-[0.2em] hover:text-secondary transition-colors py-2"
              >
                Volver al login
              </button>
            )}
          </form>
 
          {/* Footer of Card: Switch between Login/Register */}
          {authMode !== 'reset' && (
            <div className="pt-2 text-center">
              <button 
                onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                className="text-[10px] font-black text-primary-accent uppercase tracking-widest flex items-center justify-center gap-1.5 mx-auto hover:opacity-80 transition-all border border-primary-accent/20 px-4 py-2 rounded-full active:scale-95"
              >
                {authMode === 'login' ? (
                  <>¿No tienes cuenta? <span className="underline">Regístrate</span></>
                ) : (
                  <>¿Ya tienes cuenta? <span className="underline">Entrar</span></>
                )}
              </button>
            </div>
          )}
        </div>
 
        {/* Extra Info */}
        <div className="flex items-center justify-center gap-2 text-muted opacity-60">
          <AlertCircle className="w-3 h-3" />
          <p className="text-[8px] font-black uppercase tracking-[0.2em]">Servidor seguro cifrado</p>
        </div>
      </div>
    </div>
  );
}
